import React, { useState, useEffect } from "react";
import { Container, Form, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { FaShoppingCart, FaTruck, FaMoneyBillWave } from "react-icons/fa";
import vetlogo from "../assets/images/vet.png";
import jandtlogo from "../assets/images/j&t.png";
import { useTranslation } from "react-i18next";
const API_BASE_URL = process.env.REACT_APP_API_URL;

const provinces = [
  "banteay_meanchey", "battambang", "kampong_cham", "kampong_chhnang",
  "kampong_speu", "kampong_thom", "kampot", "pailin", "kandal", "koh_kong",
  "kratie", "keb", "mondulkiri", "phnom_penh", "preah_vihear", "prey_veng",
  "pursat", "siem_reap", "sihanoukville", "stung_treng", "svay_rieng",
  "takeo", "oddar_meanchey", "ratanakiri", "tboung_khmum"
];


const DeliveryForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const total = parseFloat(location.state?.subtotal) || 0;
  const cart = location.state?.cart || [];
  const buyNow = location.state?.buyNow || false;
  const [deliveryFreeLimit, setDeliveryFreeLimit] = useState(45); // Default to 45 initially
  const [showStockAlert, setShowStockAlert] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // For Buy Now, manage quantity for the first cart item
  const [quantity, setQuantity] = useState(
    buyNow && cart.length === 1
      ? cart[0].quantity || 1
      : 1
  );

  useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);

    // Additional scroll after a tiny delay to ensure everything is rendered
    const timer = setTimeout(() => {
      // Get the form or cart container
      const formContainer = document.querySelector('.delivery-form') ||
        document.querySelector('form') ||
        document.querySelector('.mt-top-cartpage') ||
        document.querySelector('.cart-items-sidebar');

      if (formContainer) {
        formContainer.scrollTop = 0;

        // Also focus on first input if it's the checkout form
        if (window.location.pathname.includes('checkout')) {
          const firstInput = document.querySelector('input, select, textarea');
          if (firstInput) {
            setTimeout(() => firstInput.focus(), 100);
          }
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Apply formatting: XXX-XXX-XXXX for 10 digits, XX-XXX-XXXX for 9 digits
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (match) {
      return [match[1], match[2], match[3]]
        .filter(group => group)
        .join('-');
    }
    return '';
  };

  useEffect(() => {
    const fetchDeliveryFreeLimit = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/delivery-free-limits`);
        const data = await response.json();
        if (data.length > 0) {
          setDeliveryFreeLimit(data[0].amount);
        }
      } catch (error) {
        console.error('Error fetching delivery free limit:', error);
      }
    };

    fetchDeliveryFreeLimit();
  }, []);

  useEffect(() => {
    if (buyNow && cart.length === 1) {
      cart[0].quantity = quantity;
    }
    // eslint-disable-next-line
  }, [quantity]);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    province: "",
    address: "",
    delivery: "",
    confirm: false,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ""
      }));
    }
  };


  const calculateDeliveryFee = () => {
    const rawSubtotal = buyNow ? getBuyNowTotal() : total;
    const currentSubtotal = Math.round(rawSubtotal * 100) / 100;

    if (currentSubtotal >= deliveryFreeLimit) return "free";
    if (!formData.province || !formData.delivery) return 0;

    // For Phnom Penh
    if (formData.province === "phnom_penh") {
      if (formData.delivery === "standard") return 1.5;
      return 2.0;
    }

    // For other provinces
    return 2.0;
  };


  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t("Name is required");

    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!phoneDigits) {
      newErrors.phone = t("Phone number is required");
    } else if (phoneDigits.length !== 9 && phoneDigits.length !== 10) {
      newErrors.phone = t("Please enter a valid 9 or 10-digit phone number");
    }

    if (!formData.province) newErrors.province = t("Please select a province");
    if (!formData.address.trim()) newErrors.address = t("Address is required");
    if (!formData.delivery) newErrors.delivery = t("Please select a delivery method");
    if (!formData.confirm) newErrors.confirm = t("You must confirm your information");

    return newErrors;
  };


  const getBuyNowTotal = () => {
    if (!cart[0] || !cart[0].product) return 0;
    const p = cart[0].product;
    const price = p.discount > 0
      ? p.price * (1 - p.discount / 100)
      : p.price;
    return price * quantity;
  };

  const deliveryFee = calculateDeliveryFee();
  const grandTotal = (buyNow ? getBuyNowTotal() : total) + (deliveryFee === "free" ? 0 : deliveryFee);

  // Need part with product link
  // In DeliveryForm.js, update the handleProceedToPayment function
  const handleProceedToPayment = () => {
    if (isProcessing) return;
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstErrorField = Object.keys(formErrors)[0];
      const errorElement = document.getElementsByName(firstErrorField)[0];
      if (errorElement) {
        errorElement.focus();
      }
      return;
    }

    // Store the phone number without hyphens
    const cleanedPhone = formData.phone.replace(/\D/g, '');

    // Ensure ALL product data including selectedColor is passed to payment
    const cartWithFullData = buyNow
      ? [{
        product: {
          ...cart[0].product,
          // Ensure image is properly passed
          image: cart[0].product.image ||
            cart[0].product.images?.[0] ||
            cart[0].product.productImage ||
            "",
          // Ensure selectedColor is passed
          selectedColor: cart[0].product.selectedColor || null
        },
        quantity
      }]
      : cart.map(item => ({
        product: {
          ...item.product,
          // Use the same logic to ensure image is properly passed
          image: item.product.image ||
            item.product.images?.[0] ||
            item.product.productImage ||
            "",
          // FIX: Ensure selectedColor is passed from cart item
          selectedColor: item.product.selectedColor || null
        },
        quantity: item.quantity
      }));

    console.log('Cart data being sent to payment:', cartWithFullData);
    console.log('First product selectedColor:', cartWithFullData[0]?.product?.selectedColor);

    // Remove any stale orderCreated flag so Payment doesn't think an order was already created
    setIsProcessing(true);

    setTimeout(() => {
      // Remove any stale order flag
      localStorage.removeItem("orderCreated");

      // Save payment data
      localStorage.setItem(
        "paymentData",
        JSON.stringify({
          formData,
          cart: cartWithFullData,
          total: grandTotal
        })
      );

      // Navigate to payment page
      navigate("/payment", {
        state: {
          formData: {
            ...formData,
            phone: cleanedPhone
          },
          cart: cartWithFullData,
          total: grandTotal,
          deliveryFee
        }
      });
    }, 3000);
  };

  return (
    <main className="delivery-form mt-5 mt-top-cartpage mt-top-cartpage" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      <section className="d-flex justify-content-center align-items-center mt-top-cartpage-top">
        <Container className="d-flex flex-column mt-top-cartpage justify-content-center align-items-center">
          <Form
            method="post"
            className="mt-3 shadow-custom form-custom py-4 px-4 rounded-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleProceedToPayment();
            }}
          >
            <div className="d-flex justify-content-center align-items-center flex-column">
              <h4 className="text-center text-dark">{t('Delivery Confirmation Form')}</h4>
              <div className="footer-line-style"></div>
            </div>
            <Form.Group className="my-4">
              <Form.Label className="form-label text-dark">{t('Your Name*')}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                className={`form-control ${errors.name ? "is-invalid" : ""}`}
                placeholder={t('Input the same name on your bank account')}
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="form-label text-dark">{t('Your Phone Number*')}</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                placeholder={t('Input your phone number for contact delivery')}
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value;
                  // Format the phone number while typing
                  const formatted = formatPhoneNumber(value);

                  // Allow up to 12 characters (for 10 digits + 2 hyphens)
                  if (formatted.length <= 12) {
                    setFormData({
                      ...formData,
                      phone: formatted
                    });
                  }

                  // Clear error if previously shown
                  if (errors.phone) {
                    setErrors(prevErrors => ({
                      ...prevErrors,
                      phone: ""
                    }));
                  }
                }}
                maxLength={12} // 10 digits + 2 hyphens
              />

              {errors.phone ? (
                <div className="invalid-feedback">{errors.phone}</div>
              ) : formData.phone.length > 0 &&
                (formData.phone.replace(/\D/g, '').length !== 9 &&
                  formData.phone.replace(/\D/g, '').length !== 10) ? (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  {t('Phone number must be 9 or 10 digits')}
                </div>
              ) : formData.phone.length > 0 &&
                (formData.phone.replace(/\D/g, '').length === 9 ||
                  formData.phone.replace(/\D/g, '').length === 10) ? (
                <div className="valid-feedback" style={{ display: 'block' }}>
                  {t('Valid phone number')}
                </div>
              ) : null}
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="form-label text-dark">{t('City / Province')}</Form.Label>
              <Form.Select
                name="province"
                className={`form-control ${errors.province ? "is-invalid" : ""}`}
                value={formData.province}
                onChange={handleChange}
              >
                <option value="" className="opacity-75">
                  {t('Select a province')}
                </option>
                {provinces.map((province, index) => (
                  <option key={index} value={province}>
                    {t(province)}
                  </option>
                ))}
              </Form.Select>
              {errors.province && (
                <div className="invalid-feedback">{errors.province}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="form-label text-dark">{t('Current Address')}</Form.Label>
              <Form.Control
                type="text"
                name="address"
                className={`form-control ${errors.address ? "is-invalid" : ""}`}
                placeholder={t('example_address')}
                value={formData.address}
                onChange={handleChange}
              />
              {errors.address && (
                <div className="invalid-feedback">{errors.address}</div>
              )}
            </Form.Group>


            <Form.Group className="mb-4">
              <Form.Label className="form-label text-dark">{t('Delivery Method')}</Form.Label>
              <div className="d-flex flex-column gap-3 mt-2">
                {formData.province === "phnom_penh" ? (
                  <>
                    {/* Cash on Delivery */}
                    <div
                      onClick={() => setFormData({ ...formData, delivery: "standard" })}
                      className={`d-flex align-items-center justify-content-between p-3 border rounded`}
                      style={{
                        cursor: "pointer",
                        borderColor: formData.delivery === "standard" ? "#007bff" : "#ccc",
                        borderWidth: "1px",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="radio"
                          name="delivery"
                          checked={formData.delivery === "standard"}
                          onChange={() => setFormData({ ...formData, delivery: "standard" })}
                        />

                        <div
                          style={{
                            backgroundColor: "#03cae0aa",
                            width: 50,
                            height: 50,
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src="https://res.cloudinary.com/dxddfydcx/image/upload/v1761151345/delivery_rvuded.png"
                            alt="Cash on Delivery Logo"
                            style={{ objectFit: "cover", borderRadius: "6px" }}
                          />
                        </div>
                        <div>
                          <div className="fw-semibold">{t('Cash on Delivery')}</div>
                          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                            {t('Pay when your order arrives')}
                          </div>
                        </div>
                      </div>
                      <div className="fw-semibold">$1.50</div>
                    </div>

                    {/* {Vtp} */}
                    <div
                      onClick={() => setFormData({ ...formData, delivery: "vtb" })}
                      className={`d-flex align-items-center justify-content-between p-3 border rounded`}
                      style={{
                        cursor: "pointer",
                        borderColor: formData.delivery === "vtb" ? "#007bff" : "#ccc",
                        borderWidth: "1px",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                      }}
                    >
                      {/* Left: Radio + Logo + Text */}
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="radio"
                          name="delivery"
                          checked={formData.delivery === "vtb"}
                          onChange={() => setFormData({ ...formData, delivery: "vtb" })}
                        />

                        <div
                          style={{
                            backgroundColor: "#ff6600",
                            width: 50,
                            height: 50,
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src="https://res.cloudinary.com/dxddfydcx/image/upload/v1761150852/vet_cjt3fx.png"
                            alt="Vireak Buntham Logo"
                            style={{ objectFit: "contain", borderRadius: "6px" }}
                          />
                        </div>
                        <div>
                          <div className="fw-semibold">{t('Vireak Buntham')}</div>
                          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                            {t('estimated_delivery')}
                          </div>
                        </div>
                      </div>

                      {/* Right: Price */}
                      <div className="fw-semibold">$2.00</div>
                    </div>

                    {/* J&T Express */}
                    <div
                      onClick={() => setFormData({ ...formData, delivery: "j&t" })}
                      className={`d-flex align-items-center justify-content-between p-3 border rounded`}
                      style={{
                        cursor: "pointer",
                        borderColor: formData.delivery === "j&t" ? "#007bff" : "#ccc",
                        borderWidth: "1px",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="radio"
                          name="delivery"
                          checked={formData.delivery === "j&t"}
                          onChange={() => setFormData({ ...formData, delivery: "j&t" })}
                        />
                        <div
                          style={{
                            backgroundColor: "#ff0000",
                            width: 50,
                            height: 50,
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src="https://res.cloudinary.com/dxddfydcx/image/upload/v1761150836/j_t_hcqcnn.png"
                            alt="J&T Express Logo"
                            style={{ objectFit: "contain", borderRadius: "6px" }}
                          />
                        </div>
                        <div>
                          <div className="fw-semibold">J&T Express</div>
                          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                            {t('estimated_delivery')}
                          </div>
                        </div>
                      </div>
                      <div className="fw-semibold">$2.00</div>
                    </div>

                  </>
                ) : (
                  <>
                    {/* {Vtp} */}
                    <div
                      onClick={() => setFormData({ ...formData, delivery: "vtb" })}
                      className={`d-flex align-items-center justify-content-between p-3 border rounded`}
                      style={{
                        cursor: "pointer",
                        borderColor: formData.delivery === "vtb" ? "#007bff" : "#ccc",
                        borderWidth: "1px",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                      }}
                    >
                      {/* Left: Radio + Logo + Text */}
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="radio"
                          name="delivery"
                          checked={formData.delivery === "vtb"}
                          readOnly
                        />
                        <div
                          style={{
                            backgroundColor: "#ff6600",
                            width: 50,
                            height: 50,
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={vetlogo}
                            alt="Vireak Buntham Logo"
                            style={{ objectFit: "contain", borderRadius: "6px" }}
                          />
                        </div>
                        <div>
                          <div className="fw-semibold">Vireak Buntham</div>
                          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                            {t('estimated_delivery')}
                          </div>
                        </div>
                      </div>

                      {/* Right: Price */}
                      <div className="fw-semibold">$2.00</div>
                    </div>
                    {/* J&T Express */}
                    <div
                      onClick={() => setFormData({ ...formData, delivery: "j&t" })}
                      className={`d-flex align-items-center justify-content-between p-3 border rounded`}
                      style={{
                        cursor: "pointer",
                        borderColor: formData.delivery === "j&t" ? "#007bff" : "#ccc",
                        borderWidth: "1px",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="radio"
                          name="delivery"
                          checked={formData.delivery === "j&t"}
                          readOnly
                        />
                        <div
                          style={{
                            backgroundColor: "#ff0000",
                            width: 50,
                            height: 50,
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={jandtlogo}
                            alt="J&T Express Logo"
                            style={{ objectFit: "contain", borderRadius: "6px" }}
                          />
                        </div>
                        <div>
                          <div className="fw-semibold">J&T Express</div>
                          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                            {t('estimated_delivery')}
                          </div>
                        </div>
                      </div>
                      <div className="fw-semibold">$2.00</div>
                    </div>
                  </>
                )}
              </div>
              {errors.delivery && (
                <div className="invalid-feedback d-block">{errors.delivery}</div>
              )}
            </Form.Group>


            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                id="confirm-info"
                name="confirm"
                label={t('confirm that my information is accurate')}
                className={`form-check-label fs-6 opacity-75 ${errors.confirm ? "is-invalid" : ""}`}
                checked={formData.confirm}
                onChange={handleChange}
              />
              {errors.confirm && (
                <div className="invalid-feedback">{errors.confirm}</div>
              )}
            </Form.Group>
            {buyNow && cart.length === 1 && (
              <div
                className="d-flex align-items-start justify-content-between gap-3 mb-3 flex-nowrap"
                style={{
                  width: '100%',
                  overflowX: 'auto',
                }}
              >
                {/* Quantity Controls */}
                <div className="d-flex align-items-start flex-column flex-shrink-0 mt-md-3 mt-0">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-pill py-1"
                    style={{
                      background: '#fff',
                      minWidth: 'fit-content',
                    }}
                  >
                    <Button
                      variant="outline-secondary"
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '36px',
                        height: '36px',
                        padding: 0,
                        fontSize: '18px',
                      }}
                      onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                      disabled={quantity === 1}
                    >
                      −
                    </Button>
                    <span
                      className="mx-md-2 mx-0 mt-3 mt-md-0 fw-semibold"
                      style={{ fontSize: '16px', minWidth: '20px', textAlign: 'center' }}
                    >
                      {quantity}
                    </span>
                    <Button
                      variant="outline-secondary"
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '36px',
                        height: '36px',
                        padding: 0,
                        fontSize: '18px',
                      }}
                      onClick={() => {
                        if (quantity < cart[0].product.Item) {
                          setQuantity(quantity + 1);
                        } else {
                          // Show alert when trying to exceed stock limit
                          setShowStockAlert(true);

                          // Auto-hide the alert after 5 seconds
                          setTimeout(() => {
                            setShowStockAlert(false);
                          }, 5000);
                        }
                      }}
                    >
                      +
                    </Button>
                  </div>

                  {/* Stock Alert Message - Show below quantity controls */}
                  {showStockAlert && (
                    <div className="alert alert-warning mt-2 mb-0 py-2" style={{ fontSize: '0.875rem' }}>
                      {t("Only")} {cart[0].product.Item} {t("items available in stock")}
                    </div>
                  )}
                </div>

                {/* Cancel / Delete Button */}
                <Button
                  variant="outline-danger mt-md-3 mt-0"
                  className="flex-shrink-0 mt-3"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingLeft: '6px',
                    paddingRight: '6px'
                  }}
                  onClick={() => navigate(-1)}
                >
                  <span className="mx-2">{t('Cancel Buy')}</span>
                </Button>
              </div>
            )}

            <div className="d-flex flex-column align-items-end">
              <div className="d-flex align-items-center mb-2 gap-2">
                <FaShoppingCart size={18} className="text-primary" />
                <span className="fw-semibold">{t('Subtotal')}</span>
                <span>
                  ${buyNow && cart.length === 1
                    ? getBuyNowTotal().toFixed(2)
                    : total.toFixed(2)
                  }
                </span>
              </div>
              <div className="d-flex align-items-center mb-2 gap-2">
                <FaTruck size={18} className="text-warning" />
                <span className="fw-semibold">{t('Delivery Fee')}</span>
                <span className={deliveryFee === "free" ? "text-success" : ""}>
                  {deliveryFee === "free"
                    ? "Free"
                    : !formData.province || !formData.delivery
                      ? "$0.00"
                      : `$${deliveryFee.toFixed(2)}`
                  }
                </span>
              </div>
              {deliveryFee === "free" && (
                <div className="text-success fw-semibold mb-2" style={{ fontSize: "0.95rem" }}>
                  {t('Your order is up to')} ${deliveryFreeLimit.toFixed(2)},{t('you get')} <b>{t('FREE delivery!')}</b>
                </div>
              )}
              <div className="line mt-2"></div>
              <div className="d-flex align-items-center gap-2 mt-3">
                <FaMoneyBillWave size={20} className="text-success" />
                <span className="fw-bold fs-5">{t('Total')}</span>
                <span className="fw-bold fs-5">
                  ${grandTotal.toFixed(2)}
                </span>
              </div>
            </div>
            <Button
              type="submit"
              className="w-100 border-0 mt-3 py-2 fw-semibold text-white"
              disabled={isProcessing}
              style={{
                background: "#000000",
                border: "none",
                borderRadius: "8px",
                letterSpacing: "0.5px",
                transition: "0.3s ease",
                fontSize: "15px",
                fontWeight: 600,
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#ff0000";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(255, 0, 0, 0.4)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "#000000";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.3)";
              }}
            >
              {isProcessing ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  {t("Processing...")}
                </>
              ) : (
                t("Proceed to Payment")
              )}
            </Button>
          </Form>
        </Container>
      </section>
    </main>
  );
};

export default DeliveryForm;

//Correct with 796 line code changes
